import {
  type AuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type User,
  type Unsubscribe,
} from "firebase/auth";

import {
  AuthenticationError,
  authenticationErrorMessage,
  normalizeAuthenticationErrorCode,
  type AuthenticationErrorCode,
  type AuthenticationUser,
} from "../../../packages/foundation/src/index.js";

export type WebAuthProviderConfiguration = {
  apple: boolean;
  google: boolean;
};

export type WebAuthenticationState =
  | { status: "initializing" }
  | { status: "unauthenticated" }
  | { status: "authenticated"; user: AuthenticationUser }
  | { status: "error"; error: AuthenticationError };

export type EmailPasswordInput = {
  email: string;
  password: string;
};

export type WebAuthenticationService = {
  watchAuthState(
    onChange: (user: AuthenticationUser | undefined) => void,
    onError: (error: AuthenticationError) => void,
  ): Unsubscribe;
  signInWithEmailPassword(input: EmailPasswordInput): Promise<void>;
  signInWithGoogle(): Promise<void>;
  signInWithApple(): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  signOut(): Promise<void>;
};

export function createFirebaseWebAuthenticationService(
  auth: Auth,
  providerConfig: WebAuthProviderConfiguration,
): WebAuthenticationService {
  return {
    watchAuthState(onChange, onError) {
      return onAuthStateChanged(
        auth,
        (user) => {
          onChange(user ? authenticationUserFromFirebaseUser(user) : undefined);
        },
        (error) => {
          onError(toAuthenticationError(error));
        },
      );
    },
    async signInWithEmailPassword(input) {
      const validationError = validateEmailPasswordInput(input);
      if (validationError) {
        throw validationError;
      }

      try {
        await signInWithEmailAndPassword(auth, input.email.trim(), input.password);
      } catch (error) {
        throw toAuthenticationError(error);
      }
    },
    async signInWithGoogle() {
      if (!providerConfig.google) {
        throw new AuthenticationError("provider_configuration_unavailable");
      }

      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");

      try {
        await signInWithFirebaseProvider(auth, provider);
      } catch (error) {
        throw toAuthenticationError(error);
      }
    },
    async signInWithApple() {
      if (!providerConfig.apple) {
        throw new AuthenticationError("provider_configuration_unavailable");
      }

      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");

      try {
        await signInWithFirebaseProvider(auth, provider);
      } catch (error) {
        throw toAuthenticationError(error);
      }
    },
    async sendPasswordReset(email) {
      const normalizedEmail = email.trim();
      if (!isValidEmail(normalizedEmail)) {
        throw new AuthenticationError("invalid_email");
      }

      try {
        await sendPasswordResetEmail(auth, normalizedEmail);
      } catch (error) {
        throw toAuthenticationError(error);
      }
    },
    async signOut() {
      try {
        await signOut(auth);
      } catch (error) {
        throw toAuthenticationError(error);
      }
    },
  };
}

export function authenticationStateFromUser(
  user: AuthenticationUser | undefined,
): WebAuthenticationState {
  return user ? { status: "authenticated", user } : { status: "unauthenticated" };
}

export function authenticationUserFromFirebaseUser(user: User): AuthenticationUser {
  return {
    uid: user.uid,
    email: user.email ?? undefined,
    emailVerified: user.emailVerified,
    providerIds: user.providerData.map((provider) => provider.providerId).sort(),
  };
}

export function isDevelopmentOnlyAuthenticationUser(
  user: AuthenticationUser,
  projectId?: string,
): boolean {
  const normalizedEmail = user.email?.trim().toLowerCase();
  if (normalizedEmail?.endsWith("@example.test")) return true;
  if (!user.uid.startsWith("dev-")) return false;

  // Cloud dev retains bootstrap UIDs after users link their real Google identity.
  return !(
    projectId === "shdeed-dev" &&
    user.emailVerified &&
    isValidEmail(normalizedEmail ?? "") &&
    user.providerIds.includes("google.com")
  );
}

export function validateEmailPasswordInput(
  input: EmailPasswordInput,
): AuthenticationError | undefined {
  if (!isValidEmail(input.email.trim())) {
    return new AuthenticationError("invalid_email");
  }

  if (input.password.length < 8) {
    return new AuthenticationError("invalid_credentials");
  }

  return undefined;
}

export function authErrorText(error: AuthenticationError): string {
  return authenticationErrorMessage(error.code);
}

export function toAuthenticationError(error: unknown): AuthenticationError {
  if (error instanceof AuthenticationError) {
    return error;
  }

  return new AuthenticationError(normalizeAuthenticationErrorCode(error));
}

export function authErrorCode(error: unknown): AuthenticationErrorCode {
  return toAuthenticationError(error).code;
}

export async function signInWithFirebaseProvider(
  auth: Auth,
  provider: AuthProvider,
): Promise<void> {
  if (shouldUseRedirectAuthFlow()) {
    await signInWithRedirect(auth, provider);
    return;
  }

  try {
    await signInWithPopup(auth, provider);
  } catch (error) {
    if (shouldRetryWithRedirectAuthFlow(error)) {
      await signInWithRedirect(auth, provider);
      return;
    }

    throw error;
  }
}

export function shouldUseRedirectAuthFlow(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;

  return (
    /iphone|ipad|ipod|android/.test(userAgent) ||
    (userAgent.includes("macintosh") && maxTouchPoints > 1)
  );
}

export function shouldRetryWithRedirectAuthFlow(error: unknown): boolean {
  if (!isRecord(error) || typeof error.code !== "string") {
    return false;
  }

  return [
    "auth/operation-not-supported-in-this-environment",
    "auth/popup-blocked",
    "operation-not-supported-in-this-environment",
    "popup-blocked",
  ].includes(error.code);
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
